import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext.jsx';
import { PERMISSIONS } from '../../lib/permissions';
import supabase from '../../lib/supabaseClient.js';
import { VISIBLE_TENANTS_OR } from '../../lib/tenantVisibility';
// Same file-share detection the scrape scorer uses, so the UI and the worker can never
// disagree about what counts as a file rather than a page.
import { isFileShareUrl } from '../../lib/shareableAssets';
import axios from 'axios';
import { Card } from '../ui/card.jsx';
import Button from '../ui/button';
import { Label } from '../ui/label';
import {
  Upload,
  FileText,
  Globe,
  Trash2,
  AlertCircle,
  X,
  Wand2,
  CheckCircle,
  Brain,
  Users,
  HelpCircle,
  BookOpen,
  Download,
  Lightbulb,
  ChevronDown,
  ChevronUp,
  RotateCcw
} from 'lucide-react';

// Educational popup shown once per user on the Knowledge screen. SurFox writes
// every message itself, so the knowledge base is for FACTS about the business,
// never scripts or canned responses. A modal (rather than an inline block) makes
// new customers stop and read it, and keeps the page uncluttered.
const KnowledgeGuidanceModal = ({ onClose }) => (
  <div
    className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
    role="dialog"
    aria-modal="true"
    onClick={onClose}
  >
    <div
      className="bg-white rounded-2xl shadow-xl max-w-3xl w-full p-8 max-h-[90vh] overflow-y-auto"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center space-x-3 mb-4">
        <div className="w-10 h-10 rounded-lg bg-blue-600 flex items-center justify-center flex-shrink-0">
          <Brain className="w-5 h-5 text-white" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900">Give SurFox AI the facts, not a script</h3>
      </div>

      <p className="text-sm text-gray-700 mb-5">
        SurFox AI writes every message itself, in real time, based on how each lead replies.
        It never reads from a script, so you never need to upload sales scripts or canned
        responses. Just tell it about your business and it runs the conversation for you.
      </p>

      <div className="grid sm:grid-cols-2 gap-8 mb-6">
        <div>
          <p className="text-xs font-semibold text-green-700 mb-2 flex items-center">
            <CheckCircle className="w-4 h-4 mr-1" /> Helpful to upload
          </p>
          <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
            <li>What you do and who you help</li>
            <li>Products, services, and pricing</li>
            <li>How your process and timelines work</li>
            <li>Common questions and how you qualify a lead</li>
            <li>Contact details and policies</li>
          </ul>
        </div>
        <div>
          <p className="text-xs font-semibold text-gray-500 mb-2 flex items-center">
            <X className="w-4 h-4 mr-1" /> You do not need
          </p>
          <ul className="text-sm text-gray-500 space-y-1 list-disc list-inside">
            <li>Sales scripts or call scripts</li>
            <li>Word-for-word responses</li>
            <li>"What to say" playbooks</li>
          </ul>
          <p className="text-xs text-gray-500 mt-2">SurFox AI generates all of that on its own.</p>
        </div>
      </div>

      <div className="flex justify-end">
        <Button onClick={onClose} className="bg-blue-600 hover:bg-blue-700 text-white px-8">
          OK, got it
        </Button>
      </div>
    </div>
  </div>
);

const KnowledgeBase = () => {
  const { user, hasPermission } = useAuth();
  
  // Permission checks
  const canViewKnowledgeBase = hasPermission(PERMISSIONS.VIEW_AI_BUNDLE_PREVIEW) || hasPermission(PERMISSIONS.UPLOAD_KNOWLEDGE_BASE_DOCS);
  const canUploadDocs = hasPermission(PERMISSIONS.UPLOAD_KNOWLEDGE_BASE_DOCS);
  const canDeleteDocs = hasPermission(PERMISSIONS.DELETE_KB_DOCS);
  const isGlobalAdmin = user?.role === 'global_admin';

  // State
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [kbActiveTab, setKbActiveTab] = useState('documents');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [websiteTitle, setWebsiteTitle] = useState('');
  const [addingWebsite, setAddingWebsite] = useState(false);
  const [websites, setWebsites] = useState([]);
  // "Is this intel to discuss, or a document to send?" — asked when the pasted URL is a
  // file or a file-share, because those two intents need opposite handling and we used
  // to silently assume the first. Exodus pasted a Drive FOLDER of photos here; we
  // scraped it, stored a Google sign-in form as their campaign knowledge, marked it
  // complete, and they spent about an hour working out why the link was not being
  // shared. Answering "send" routes to the same shareable_assets store Surf writes to.
  const [assetPrompt, setAssetPrompt] = useState(null); // null | { url, title }
  const [assetCampaigns, setAssetCampaigns] = useState([]);
  const [assetCampaignId, setAssetCampaignId] = useState('');
  const [assetLabel, setAssetLabel] = useState('');
  const [assetSendWhen, setAssetSendWhen] = useState('');
  const [savingAsset, setSavingAsset] = useState(false);
  // Recently deleted docs (30-day recycle bin). Restoring brings back the file
  // AND its original campaign scoping.
  const [deletedDocs, setDeletedDocs] = useState([]);
  const [restoringId, setRestoringId] = useState(null);
  const [processingWebsites, setProcessingWebsites] = useState(new Set());
  const [dataLoading, setDataLoading] = useState(true);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generatedKB, setGeneratedKB] = useState('');
  const [uploadProgress, setUploadProgress] = useState({});
  
  // Global Admin State
  const [allTenants, setAllTenants] = useState([]);
  const [selectedTenantId, setSelectedTenantId] = useState(null);
  const [loadingTenants, setLoadingTenants] = useState(false);
  
  // Tutorial/Guide State
  const [showGuide, setShowGuide] = useState(true);
  const [expandedGuideSection, setExpandedGuideSection] = useState('what-is');

  // "Facts, not a script" education popup — auto-shown once per user, reopenable.
  const [showGuidance, setShowGuidance] = useState(false);
  useEffect(() => {
    try {
      if (!localStorage.getItem('surfox_kb_guidance_seen')) setShowGuidance(true);
    } catch { /* localStorage unavailable — skip auto-show */ }
  }, []);
  const dismissGuidance = () => {
    try { localStorage.setItem('surfox_kb_guidance_seen', '1'); } catch { /* ignore */ }
    setShowGuidance(false);
  };

  // Tenant industry (for recommended template)
  const [tenantIndustry, setTenantIndustry] = useState(null);

  // AI industry from platform_settings — drives talk track selector visibility
  const [aiIndustry, setAiIndustry] = useState('');
  const [selectedTalkTrack, setSelectedTalkTrack] = useState('');

  const STAFFING_TALK_TRACKS = [
    { value: 'recruiting_candidates', label: 'Recruiting Candidates' },
    { value: 'recruiting_candidates_multi_role', label: 'Recruiting Candidates - Multi Role (KB-driven)' },
    { value: 'acquiring_clients', label: 'B2B Client Acquisition' },
    { value: 'credentialing_services', label: 'Credentialing Services' },
  ];

  const showTalkTrackSelector = aiIndustry === 'Staffing';

  // Alerts
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Load tenants for global admin
  useEffect(() => {
    if (isGlobalAdmin) {
      loadAllTenants();
    }
  }, [isGlobalAdmin]);

  // Global admin defaults to their OWN tenant's knowledge base — the dropdown
  // is how they switch to someone else, not a prerequisite for seeing anything.
  useEffect(() => {
    if (isGlobalAdmin && user?.tenant_id && !selectedTenantId) {
      setSelectedTenantId(user.tenant_id);
    }
  }, [isGlobalAdmin, user?.tenant_id, selectedTenantId]);

  // Fetch tenant industry for template recommendation
  useEffect(() => {
    const fetchTenantIndustry = async () => {
      if (!user?.tenant_id) return;
      const { data } = await supabase
        .from('tenants')
        .select('industries(name)')
        .eq('id', user.tenant_id)
        .single();
      if (data?.industries?.name) setTenantIndustry(data.industries.name);
    };
    fetchTenantIndustry();
  }, [user?.tenant_id]);

  // Fetch AI industry from platform_settings — drives talk track selector
  useEffect(() => {
    const fetchAiIndustry = async () => {
      if (!user?.tenant_id) return;
      const { data } = await supabase
        .from('platform_settings')
        .select('value')
        .eq('tenant_id', user.tenant_id)
        .eq('key', 'industry')
        .single();
      if (data?.value) setAiIndustry(data.value);
    };
    fetchAiIndustry();
  }, [user?.tenant_id]);

  // Load knowledge base data
  useEffect(() => {
    if (canViewKnowledgeBase) {
      fetchKnowledgeBase();
      fetchWebsites();
      fetchDeletedDocs();
    }
  }, [canViewKnowledgeBase, selectedTenantId]);

  const loadAllTenants = async () => {
    try {
      setLoadingTenants(true);
      const { data, error } = await supabase
        .from('tenants')
        .select('id, name, created_at')
        .or(VISIBLE_TENANTS_OR) // hide inactive/suspended from global_admin dropdown
        .order('name');

      if (error) throw error;
      setAllTenants(data || []);
    } catch (err) {
      console.error('Error loading tenants:', err);
    } finally {
      setLoadingTenants(false);
    }
  };

  const fetchKnowledgeBase = async () => {
    if (!canViewKnowledgeBase) return;

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) return console.error('No auth token found');

      // Use selectedTenantId for global admins, otherwise use user's tenant_id
const targetTenantId = isGlobalAdmin && selectedTenantId 
  ? selectedTenantId 
  : user.tenant_id;

// Fetch from knowledge_base for Documents tab
const res = await axios.get(`${process.env.REACT_APP_API_URL}/knowledge/docs`, {
  headers: {
    Authorization: `Bearer ${token}`,
    // Pass target tenant if global admin
    ...(isGlobalAdmin && selectedTenantId && { 'x-tenant-id': selectedTenantId })
  },
  params: {
    ...(isGlobalAdmin && selectedTenantId && { tenant_id: selectedTenantId })
  }
});

      const docsData = Array.isArray(res.data) ? res.data : res.data.data || [];
      
      // Filter out websites
      const documentsOnly = docsData.filter(doc =>
        doc.source_type !== 'website' &&
        !doc.website_url &&
        doc.file_url && 
        !doc.title?.startsWith('http')
      );

      setDocuments(documentsOnly);
      setDataLoading(false);
    } catch (error) {
      console.error('Error fetching knowledge base:', error);
      setDataLoading(false);
    }
  };

  const fetchWebsites = async () => {
    if (!canViewKnowledgeBase) return;

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;

      const res = await axios.get(`${process.env.REACT_APP_API_URL}/knowledge/docs`, {
        headers: { 
          Authorization: `Bearer ${token}`,
          ...(isGlobalAdmin && selectedTenantId && { 'x-tenant-id': selectedTenantId })
        },
        params: {
          source_type: 'website',
          ...(isGlobalAdmin && selectedTenantId && { tenant_id: selectedTenantId })
        }
      });

      const websitesData = Array.isArray(res.data) ? res.data : res.data.data || [];
      setWebsites(websitesData);
    } catch (error) {
      console.error('Error fetching websites:', error);
    }
  };

  const handleFileChange = (e) => {
    setSelectedFile(e.target.files[0]);
  };

  const handleUpload = async () => {
    if (!canUploadDocs && !isGlobalAdmin) {
      setError("You don't have permission to upload documents.");
      return;
    }

    if (!selectedFile) {
      setError('No file selected.');
      return;
    }

    // Determine which tenant_id to use
    const targetTenantId = isGlobalAdmin && selectedTenantId 
      ? selectedTenantId 
      : user.tenant_id;

    if (!targetTenantId) {
      setError('No tenant selected');
      return;
    }

    setUploading(true);
    setError('');
    setSuccess('');

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) throw new Error('No authentication token found');

      const tenant_id = targetTenantId;
      const filePath = `${tenant_id}/${selectedFile.name}`;
      const { data: uploadData, error: uploadError } = await supabase
        .storage
        .from('documents')
        .upload(filePath, selectedFile, { cacheControl: '3600', upsert: true });

      if (uploadError) throw uploadError;

      const { data: publicData } = supabase
        .storage
        .from('documents')
        .getPublicUrl(filePath);

      const file_url = publicData.publicUrl;
      const file_name = selectedFile.name;
      const title = file_name.replace(/\.[^/.]+$/, '');

      const uploadPayload = {
        file_url,
        file_name,
        title,
        description: '',
        tenant_id: targetTenantId,
        talk_track_type: selectedTalkTrack || null
      };

      // Regular upload - goes to knowledge_base and triggers embedding
      const uploadResponse = await axios.post(
        `${process.env.REACT_APP_API_URL}/knowledge/upload`,
        uploadPayload,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const docId = uploadResponse.data?.record?.id;
      if (!docId) throw new Error('Upload succeeded but document ID missing');

      setSuccess(isGlobalAdmin 
        ? 'Document uploaded successfully for selected tenant!' 
        : 'File uploaded successfully! Processing will begin automatically.');
      setSelectedFile(null);
      setSelectedTalkTrack('');
      const fileInput = document.querySelector('input[type="file"]');
      if (fileInput) fileInput.value = '';
      fetchKnowledgeBase();
      setTimeout(() => setSuccess(''), 3000);

    } catch (err) {
      console.error('Upload error:', err);
      setError(err.response?.data?.error || err.message || 'Failed to upload document');
      setUploading(false);
    } finally {
      setUploading(false);
    }
  };

  // Campaigns that can hold a sendable link (they need a talk track to store it on).
  const loadAssetCampaigns = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      const q = isGlobalAdmin && selectedTenantId ? `?tenant_id=${selectedTenantId}` : '';
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/knowledge/shareable-asset/campaigns${q}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAssetCampaigns(res.data?.campaigns || []);
    } catch (err) {
      setAssetCampaigns([]);
    }
  };

  // "A document to send" — store the link plus WHEN to send it, on the campaign's
  // talk track, through the same path Surf writes to. Nothing is scraped.
  const handleSaveShareableAsset = async () => {
    if (!assetCampaignId) { setError('Pick which campaign should send this.'); return; }
    if (!assetSendWhen.trim()) { setError('Tell us when the AI should send it.'); return; }

    setSavingAsset(true);
    setError('');
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/knowledge/shareable-asset`,
        {
          campaign_id: assetCampaignId,
          label: assetLabel || assetPrompt?.title || 'Shared link',
          url: assetPrompt?.url,
          send_when: assetSendWhen.trim(),
          ...(isGlobalAdmin && selectedTenantId ? { tenant_id: selectedTenantId } : {}),
        },
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      setSuccess(`Saved. The AI will send this on ${res.data?.campaign_name || 'that campaign'} when a lead matches what you described.`);
      setAssetPrompt(null);
      setWebsiteUrl('');
      setWebsiteTitle('');
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Could not save that link.');
    } finally {
      setSavingAsset(false);
    }
  };

  const handleAddWebsite = async (skipAssetCheck = false) => {
    if (!canUploadDocs && !isGlobalAdmin) {
      setError("You don't have permission to add websites.");
      return;
    }

    if (!websiteUrl.trim()) {
      setError('Website URL is required.');
      return;
    }

    // Interrupt BEFORE scraping when the URL is a file or a file-share. `skipAssetCheck`
    // is how "intel to discuss" comes back through and proceeds normally, so the question
    // is asked once and never blocks a tenant who meant exactly what we assumed.
    // Detection is a HINT, not a gate: being wrong costs one extra click, and the old
    // behaviour of guessing silently cost a tenant an hour.
    if (!skipAssetCheck && isFileShareUrl(websiteUrl.trim())) {
      let url = websiteUrl.trim();
      if (!url.startsWith('http://') && !url.startsWith('https://')) url = 'https://' + url;
      setAssetLabel(websiteTitle || '');
      setAssetSendWhen('');
      setAssetCampaignId('');
      setAssetPrompt({ url, title: websiteTitle });
      loadAssetCampaigns();
      return;
    }

    const targetTenantId = isGlobalAdmin && selectedTenantId 
      ? selectedTenantId 
      : user.tenant_id;

    if (!targetTenantId) {
      setError('No tenant selected');
      return;
    }

    setAddingWebsite(true);
    setError('');
    setSuccess('');

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) throw new Error('No authentication token found');

      let url = websiteUrl.trim();
      if (!url.startsWith('http://') && !url.startsWith('https://')) {
        url = 'https://' + url;
      }

      const websitePayload = {
        website_url: url,
        title: websiteTitle || url,
        description: '',
        source_type: 'website',
        tenant_id: targetTenantId,
        talk_track_type: selectedTalkTrack || null
      };

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/knowledge/upload`,
        websitePayload,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const websiteId = response.data?.record?.id;
      if (!websiteId) throw new Error('Website added but ID missing');

      setSuccess(isGlobalAdmin 
        ? 'Website added successfully for selected tenant! Scraping will begin automatically.' 
        : 'Website added successfully! Scraping will begin automatically.');
      setWebsiteUrl('');
      setWebsiteTitle('');
      
      fetchWebsites();
      setTimeout(() => setSuccess(''), 3000);

      const scrapeInterval = setInterval(async () => {
        try {
          const checkRes = await axios.get(
            `${process.env.REACT_APP_API_URL}/knowledge/docs`,
            {
              headers: { Authorization: `Bearer ${token}` }
            }
          );

          const checkData = Array.isArray(checkRes.data) ? checkRes.data : checkRes.data.data || [];
          const thisWebsite = checkData.find(w => w.id === websiteId);

          if (thisWebsite && thisWebsite.content) {
            fetchWebsites();
            clearInterval(scrapeInterval);
          }
        } catch (err) {
          console.error('Error checking scrape status:', err);
        }
      }, 3000);

      setTimeout(() => clearInterval(scrapeInterval), 60000);

    } catch (err) {
      console.error('Add website error:', err);
      setError(err.response?.data?.error || err.message || 'Failed to add website');
    } finally {
      setAddingWebsite(false);
    }
  };

  const handleDeleteWebsite = async (websiteId) => {
    if (!canDeleteDocs) {
      setError("You don't have permission to delete websites.");
      return;
    }

    if (!window.confirm('Are you sure you want to delete this website?')) return;

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;

      await axios.delete(
        `${process.env.REACT_APP_API_URL}/knowledge/docs/${websiteId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      setSuccess('Website deleted successfully');
      fetchWebsites();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Delete website error:', err);
      setError('Failed to delete website');
    }
  };

  const handleToggleMaster = async (docId, currentValue) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;

      await axios.patch(
        `${process.env.REACT_APP_API_URL}/knowledge/docs/${docId}/master`,
        { is_master: !currentValue },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setDocuments(prev => prev.map(doc =>
        doc.id === docId ? { ...doc, is_master: !currentValue } : doc
      ));
    } catch (err) {
      console.error('Toggle master error:', err);
      setError('Failed to update master status');
    }
  };

  const handleDelete = async (docId) => {
    if (!canDeleteDocs) {
      setError("You don't have permission to delete documents.");
      return;
    }

    if (!window.confirm('Are you sure you want to delete this document?')) return;

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;

      await axios.delete(
        `${process.env.REACT_APP_API_URL}/knowledge/docs/${docId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      setSuccess('Document deleted. You can restore it for the next 30 days from "Recently deleted" below.');
      fetchKnowledgeBase();
      fetchDeletedDocs();
      setTimeout(() => setSuccess(''), 6000);
    } catch (err) {
      console.error('Delete error:', err);
      setError('Failed to delete document');
    }
  };

  const fetchDeletedDocs = async () => {
    if (!canViewKnowledgeBase) return;

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) return;

      const res = await axios.get(`${process.env.REACT_APP_API_URL}/knowledge/trash`, {
        headers: {
          Authorization: `Bearer ${token}`,
          ...(isGlobalAdmin && selectedTenantId && { 'x-tenant-id': selectedTenantId })
        },
        params: {
          ...(isGlobalAdmin && selectedTenantId && { tenant_id: selectedTenantId })
        }
      });

      setDeletedDocs(res.data?.documents || []);
    } catch (err) {
      // A missing recycle bin should never block the main knowledge base view.
      console.error('Error fetching deleted documents:', err);
    }
  };

  const handleRestore = async (archiveId) => {
    if (!canDeleteDocs) {
      setError("You don't have permission to restore documents.");
      return;
    }

    setRestoringId(archiveId);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;

      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/knowledge/trash/${archiveId}/restore`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const { campaigns_relinked = 0, campaigns_missing = 0, searchable, warning } = res.data || {};

      if (warning) {
        // Restored but not indexed: the doc is visible yet the AI cannot use it.
        setError(warning);
      } else {
        let msg = 'Document restored';
        if (campaigns_relinked > 0) {
          msg += ` and put back on ${campaigns_relinked} campaign${campaigns_relinked === 1 ? '' : 's'}`;
        }
        if (campaigns_missing > 0) {
          msg += `. ${campaigns_missing} original campaign${campaigns_missing === 1 ? ' no longer exists' : 's no longer exist'}`;
        }
        setSuccess(`${msg}.`);
        setTimeout(() => setSuccess(''), 6000);
      }

      if (searchable === false && !warning) {
        setError('Document restored, but it is not searchable yet.');
      }

      fetchKnowledgeBase();
      fetchWebsites();
      fetchDeletedDocs();
    } catch (err) {
      console.error('Restore error:', err);
      setError(err.response?.data?.error || 'Failed to restore document');
    } finally {
      setRestoringId(null);
    }
  };

  const handleBulkFileChange = (e) => {
    const newFiles = Array.from(e.target.files);
    
    // Combine with existing files
    const combinedFiles = [...selectedFiles, ...newFiles];
    
    if (combinedFiles.length > 100) {
      setError('Maximum 100 files allowed at once');
      return;
    }

    const validFiles = combinedFiles.filter(file => {
      const validExtensions = ['.pdf', '.docx'];
      const fileExt = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));
      return validExtensions.includes(fileExt);
    });

    if (validFiles.length < combinedFiles.length) {
      setError(`${combinedFiles.length - validFiles.length} file(s) skipped. Only PDF and DOCX allowed.`);
    }

    setSelectedFiles(validFiles);
    
    // Clear the input so the same file can be added again if needed
    e.target.value = '';
  };

  const handleBulkUpload = async () => {
    const targetTenantId = isGlobalAdmin && selectedTenantId 
      ? selectedTenantId 
      : user.tenant_id;

    if (!targetTenantId) {
      setError('No tenant selected');
      return;
    }

    setBulkUploading(true);
    setError('');

    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;

    // Upload each file independently so one bad file never aborts the batch.
    // Returns a summary so the caller (generate) can decide whether to proceed.
    const failed = [];
    let uploaded = 0;

    for (let i = 0; i < selectedFiles.length; i++) {
      const file = selectedFiles[i];
      try {
        const filePath = `${targetTenantId}/${file.name}`;

        // upsert so re-uploading the same filename doesn't crash on a duplicate.
        const { error: storageErr } = await supabase.storage
          .from('documents')
          .upload(filePath, file, { upsert: true });
        if (storageErr) throw storageErr;

        const { data: publicData } = supabase.storage.from('documents').getPublicUrl(filePath);

        // Upload to STAGING (not knowledge_base)
        await axios.post(`${process.env.REACT_APP_API_URL}/knowledge/upload-staging`, {
          file_url: publicData.publicUrl,
          file_name: file.name,
          title: file.name.replace(/\.[^/.]+$/, ''),
          description: '',
          tenant_id: targetTenantId
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });

        uploaded += 1;
        setUploadProgress(prev => ({ ...prev, [i]: 100 }));
      } catch (err) {
        console.error(`Upload failed for ${file.name}:`, err);
        failed.push(file.name);
        setUploadProgress(prev => ({ ...prev, [i]: -1 }));
      }
    }

    setBulkUploading(false);

    if (failed.length) {
      setError(`${uploaded} uploaded, ${failed.length} failed: ${failed.slice(0, 5).join(', ')}${failed.length > 5 ? '…' : ''}`);
    } else {
      setSuccess('All files uploaded to staging successfully!');
      setTimeout(() => setSuccess(''), 3000);
    }

    return { uploaded, failed };
  };

  const handleGenerateKB = async () => {
    if (!selectedFiles.length) {
      setError('Please select files first');
      return;
    }

    setGenerating(true);
    setError('');

    try {
      const uploadResult = await handleBulkUpload();

      // Nothing made it to staging — stop before hitting generate (which would
      // just 422). handleBulkUpload already set an explanatory error.
      if (uploadResult && uploadResult.uploaded === 0) {
        setGenerating(false);
        return;
      }

      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/knowledge/generate-kb`,
        {
          tenant_id: isGlobalAdmin && selectedTenantId ? selectedTenantId : user.tenant_id
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      setGeneratedKB(response.data.knowledge_base || 'Generated knowledge base will appear here...');
      setSuccess('Knowledge base generated successfully!');
    } catch (err) {
      console.error('Generate KB error:', err);
      setError('Failed to generate knowledge base');
    } finally {
      setGenerating(false);
    }
  };

  const handleSaveKB = async () => {
    if (!generatedKB) {
      setError('No knowledge base to save');
      return;
    }

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;

      await axios.post(
        `${process.env.REACT_APP_API_URL}/knowledge/save-master-kb`,
        {
          tenant_id: isGlobalAdmin && selectedTenantId ? selectedTenantId : user.tenant_id,
          knowledge_base: generatedKB
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      setSuccess('Knowledge base saved successfully!');
      setGeneratedKB('');
      setSelectedFiles([]);
      setUploadProgress({});
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Save KB error:', err);
      setError('Failed to save knowledge base');
    }
  };

  // Download Sample DOC Template
  // Download Sample DOC Template
const handleDownloadTemplate = async (type = 'b2b') => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;

    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/knowledge/sample-template?type=${type}`,
      {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      }
    );

    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    const filename = type === 'healthcare-credentialing'
      ? 'SurFox_Healthcare_Credentialing_Template.docx'
      : type === 'candidate'
      ? 'SurFox_Candidate_KB_Template.docx'
      : type === 'real-estate-investor'
      ? 'SurFox_Real_Estate_Investor_Template.docx'
      : 'SurFox_Knowledge_Base_Template.docx';
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    
    setSuccess(`Template downloaded! Fill it out and upload it back.`);
    setTimeout(() => setSuccess(''), 3000);
  } catch (err) {
    console.error('Download template error:', err);
    setError('Failed to download template');
  }
};

// Keep backward compatibility (if called without args)
const handleDownloadSamplePDF = () => handleDownloadTemplate('b2b');

// All available templates
const ALL_TEMPLATES = [
  { type: 'real-estate-investor', label: 'Real Estate Investor', color: 'blue', textColor: 'text-blue-600 hover:text-blue-800', btnClass: 'border-blue-300 text-blue-700 hover:bg-blue-50' },
  { type: 'b2b', label: 'Sales / B2B', color: 'orange', textColor: 'text-orange-600 hover:text-orange-800', btnClass: 'border-orange-300 text-orange-700 hover:bg-orange-50' },
  { type: 'candidate', label: 'Recruiting / Candidates', color: 'purple', textColor: 'text-purple-600 hover:text-purple-800', btnClass: 'border-purple-300 text-purple-700 hover:bg-purple-50' },
  { type: 'healthcare-credentialing', label: 'Healthcare Credentialing', color: 'green', textColor: 'text-green-600 hover:text-green-800', btnClass: 'border-green-300 text-green-700 hover:bg-green-50' },
];

// Map tenant industry name → template type
const getIndustryTemplate = (industry) => {
  if (!industry) return null;
  const lower = industry.toLowerCase();
  if (lower.includes('real estate')) return 'real-estate-investor';
  if (lower.includes('staffing') || lower.includes('recruiting')) return 'candidate';
  if (lower.includes('healthcare') || lower.includes('medical') || lower.includes('credentialing')) return 'healthcare-credentialing';
  return 'b2b';
};

const industryTemplate = getIndustryTemplate(tenantIndustry);
// If we know the industry, show only their template. Otherwise show all.
const visibleTemplates = industryTemplate
  ? ALL_TEMPLATES.filter(t => t.type === industryTemplate)
  : ALL_TEMPLATES;

  // TenantSelector component
  const TenantSelector = () => {
    if (!isGlobalAdmin) return null;

    // Own tenant first, and guaranteed present even if the visibility filter
    // ever drops it — otherwise the select renders blank against live state.
    const ownTenant = allTenants.find(t => t.id === user?.tenant_id);
    const tenantOptions = ownTenant || !user?.tenant_id
      ? [...allTenants].sort((a, b) => (a.id === user?.tenant_id ? -1 : b.id === user?.tenant_id ? 1 : 0))
      : [{ id: user.tenant_id, name: 'Your account' }, ...allTenants];

    return (
      <Card className="p-5 mb-4 bg-gradient-to-r from-purple-50 to-blue-50 border-purple-200">
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2 text-purple-700">
            <Users className="w-5 h-5" />
            <span className="font-semibold">Global Admin Mode</span>
          </div>
          
          <div className="flex-1">
            <Label className="text-sm text-gray-700 mb-1">Viewing Knowledge Base For</Label>
            <select
              value={selectedTenantId || ''}
              onChange={(e) => setSelectedTenantId(e.target.value)}
              disabled={loadingTenants}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            >
              {tenantOptions.map((tenant) => (
                <option key={tenant.id} value={tenant.id}>
                  {tenant.name}{tenant.id === user?.tenant_id ? ' (your account)' : ''}
                </option>
              ))}
            </select>
          </div>

          {selectedTenantId && selectedTenantId !== user?.tenant_id && (
            <div className="text-sm text-purple-700 bg-purple-100 px-3 py-2 rounded-lg">
              Managing another tenant
            </div>
          )}
        </div>

        <p className="text-xs text-gray-600 mt-3">
          {selectedTenantId && selectedTenantId !== user?.tenant_id
            ? '⚠️ You are viewing and uploading on behalf of another tenant. All uploads will be associated with their account.'
            : 'Showing your own documents. Switch tenants above to view or upload for someone else.'}
        </p>
      </Card>
);
  };

  // Knowledge Base Guide Component
  const KnowledgeBaseGuide = () => {
    const sections = [
      {
        id: 'what-is',
        title: 'What is the Knowledge Base?',
        icon: <HelpCircle className="w-5 h-5" />,
        content: (
          <div className="space-y-3 text-sm text-gray-700">
            <p>
              Your Knowledge Base is your AI agent's brain. When your AI texts with leads, it pulls information 
              from these documents to give accurate, helpful responses.
            </p>
            <p>
              <span className="font-medium text-gray-900">Without a knowledge base:</span> Your AI gives generic responses and may not know 
              specifics about your business, pricing, or services.
            </p>
            <p>
              <span className="font-medium text-gray-900">With a good knowledge base:</span> Your AI can answer questions like "How much does it cost?", 
              "What are your hours?", or "Do you offer benefits?" with real, accurate information.
            </p>
          </div>
        )
      },
      {
        id: 'what-to-upload',
        title: 'What Should I Upload?',
        icon: <FileText className="w-5 h-5" />,
        content: (
          <div className="space-y-3 text-sm text-gray-700">
            <p className="font-medium text-gray-900">Upload documents that answer questions your leads actually ask:</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <div className="flex items-start space-x-2">
                <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                <span>FAQs and common questions</span>
              </div>
              <div className="flex items-start space-x-2">
                <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                <span>Pricing and service details</span>
              </div>
              <div className="flex items-start space-x-2">
                <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                <span>Company info and benefits</span>
              </div>
              <div className="flex items-start space-x-2">
                <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                <span>Process / how it works</span>
              </div>
              <div className="flex items-start space-x-2">
                <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                <span>Requirements or qualifications</span>
              </div>
              <div className="flex items-start space-x-2">
                <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                <span>Objection handlers</span>
              </div>
            </div>
            <div className="mt-3 p-3 bg-red-50 rounded-lg border border-red-100">
              <p className="font-medium text-red-800 text-xs mb-1">What NOT to upload:</p>
              <p className="text-red-700 text-xs">Internal HR policies, legal documents, or anything you wouldn't want quoted to a lead.</p>
            </div>
          </div>
        )
      },
      {
        id: 'good-doc',
        title: 'What Makes a Good Document?',
        icon: <Lightbulb className="w-5 h-5" />,
        content: (
          <div className="space-y-3 text-sm text-gray-700">
            <p>Not all documents are created equal. Here's how to create documents your AI can actually use:</p>
            
            <div className="space-y-2">
              <div className="p-3 bg-green-50 rounded-lg border border-green-100">
                <p className="font-medium text-green-800 text-xs mb-2">✓ DO THIS</p>
                <ul className="text-green-700 text-xs space-y-1">
                  <li>• Use Q&A format – questions and direct answers</li>
                  <li>• Be specific: "$18-22/hr for warehouse roles" beats "competitive pay"</li>
                  <li>• Keep documents focused – one topic per doc works best</li>
                  <li>• Write like you'd talk to a lead, not like legal wrote it</li>
                  <li>• Include real numbers, names, locations, and dates</li>
                </ul>
              </div>
              
              <div className="p-3 bg-red-50 rounded-lg border border-red-100">
                <p className="font-medium text-red-800 text-xs mb-2">✗ AVOID THIS</p>
                <ul className="text-red-700 text-xs space-y-1">
                  <li>• Dense paragraphs of text (hard for AI to parse)</li>
                  <li>• Vague language: "great benefits" – what benefits specifically?</li>
                  <li>• 50-page PDFs with buried information</li>
                  <li>• Jargon or internal terminology leads won't understand</li>
                </ul>
              </div>
            </div>

            <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-100">
              <p className="font-medium text-blue-800 text-xs mb-1">💡 Pro tip</p>
              <p className="text-blue-700 text-xs">
                A simple one-page FAQ in Q&A format will outperform a 47-page employee handbook every time. 
                Quality over quantity.
              </p>
            </div>
          </div>
        )
      }
    ];

    return (
      <Card className="mb-6 border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50">
        <div 
          className="p-4 flex items-center justify-between cursor-pointer"
          onClick={() => setShowGuide(!showGuide)}
        >
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center">
              <BookOpen className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Knowledge Base Guide</h3>
              <p className="text-sm text-gray-600">Learn how to set up your AI for success</p>
            </div>
          </div>
          <button className="p-2 hover:bg-white/50 rounded-lg transition-colors">
            {showGuide ? <ChevronUp className="w-5 h-5 text-gray-600" /> : <ChevronDown className="w-5 h-5 text-gray-600" />}
          </button>
        </div>

        {showGuide && (
          <div className="px-4 pb-4">
            <div className="border-t border-blue-200 pt-4">
              {/* Section Tabs */}
              <div className="flex space-x-2 mb-4 overflow-x-auto pb-2">
                {sections.map((section) => (
                  <button
                    key={section.id}
                    onClick={() => setExpandedGuideSection(section.id)}
                    className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                      expandedGuideSection === section.id
                        ? 'bg-white text-blue-700 shadow-sm border border-blue-200'
                        : 'text-gray-600 hover:bg-white/50'
                    }`}
                  >
                    {section.icon}
                    <span>{section.title}</span>
                  </button>
                ))}
              </div>

              {/* Active Section Content */}
              <div className="bg-white rounded-xl p-4 border border-blue-100">
                {sections.find(s => s.id === expandedGuideSection)?.content}
              </div>

 {/* Download Template CTA */}
<div className="mt-4 p-4 bg-white rounded-xl border border-blue-100">
  <div className="flex items-center justify-between flex-wrap gap-3">
    <div className="flex items-center space-x-3">
      <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
        <Download className="w-5 h-5 text-orange-600" />
      </div>
      <div>
        <p className="font-medium text-gray-900">Need a starting point?</p>
        <p className="text-sm text-gray-600">Download a template with all the sections you need</p>
      </div>
    </div>
    <div className="flex items-center flex-wrap gap-2">
      {visibleTemplates.map(({ type, label, btnClass }) => (
        <Button
          key={type}
          onClick={() => {
            handleDownloadTemplate(type);
            window.dispatchEvent(new CustomEvent('tour-template-downloaded'));
          }}
          variant="outline"
          data-tour="download-template"
          className={`tour-download-template ${btnClass}`}
        >
          <Download className="w-4 h-4 mr-2" />
          {label}
        </Button>
      ))}
    </div>
  </div>
</div>
            </div>
          </div>
        )}
      </Card>
    );
  };

  if (!canViewKnowledgeBase) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Access Restricted</h3>
        <p className="text-gray-600">You don't have permission to view the knowledge base.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center space-x-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
          <span className="text-red-800">{error}</span>
          <button onClick={() => setError('')} className="ml-auto">
            <X className="w-4 h-4 text-red-600" />
          </button>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center space-x-3">
          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
          <span className="text-green-800 font-medium">{success}</span>
          <button onClick={() => setSuccess('')} className="ml-auto">
            <X className="w-4 h-4 text-green-600" />
          </button>
        </div>
      )}

      {!canUploadDocs && !isGlobalAdmin && canViewKnowledgeBase && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-center space-x-3">
          <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0" />
          <span className="text-yellow-800">
            You have read-only access to the knowledge base. Admin permissions required to upload documents or add websites.
          </span>
        </div>
      )}

      <TenantSelector />

      {/* Knowledge Base Guide */}
      <KnowledgeBaseGuide />

      <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg">
        <button
          onClick={() => setKbActiveTab('documents')}
          className={`flex-1 flex items-center justify-center space-x-2 px-4 py-2 rounded-md transition-colors ${
            kbActiveTab === 'documents'
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <FileText className="w-4 h-4" />
          <span className="font-medium">Documents</span>
        </button>
        <button
          onClick={() => setKbActiveTab('websites')}
          className={`flex-1 flex items-center justify-center space-x-2 px-4 py-2 rounded-md transition-colors ${
            kbActiveTab === 'websites'
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <Globe className="w-4 h-4" />
          <span className="font-medium">Websites</span>
        </button>
        <button
          onClick={() => setKbActiveTab('builder')}
          className={`flex-1 flex items-center justify-center space-x-2 px-4 py-2 rounded-md transition-colors ${
            kbActiveTab === 'builder'
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <Wand2 className="w-4 h-4" />
          <span className="font-medium">AI Builder</span>
        </button>
      </div>

      <div className="flex justify-end">
        <button
          onClick={() => setShowGuidance(true)}
          className="text-xs text-gray-500 hover:text-blue-600 flex items-center space-x-1"
        >
          <HelpCircle className="w-3.5 h-3.5" />
          <span>What should I upload?</span>
        </button>
      </div>

      {showGuidance && <KnowledgeGuidanceModal onClose={dismissGuidance} />}

{kbActiveTab === 'documents' && (
  <Card className="p-5 space-y-4">
    {/* Contextual help for Documents tab */}
<div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
  <div className="flex items-start space-x-3">
    <Lightbulb className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
    <div>
      <p className="text-sm text-gray-700">
        <span className="font-medium">Upload PDFs or DOCX files</span> containing FAQs, pricing info, service details, or anything your AI should know when talking to leads.
      </p>
      <div className="mt-2 flex flex-wrap items-center gap-3">
        {visibleTemplates.map(({ type, label, textColor }, i) => (
          <React.Fragment key={type}>
            <button
              onClick={() => {
                handleDownloadTemplate(type);
                window.dispatchEvent(new CustomEvent('tour-template-downloaded'));
              }}
              className={`tour-download-template text-sm font-medium flex items-center space-x-1 ${textColor}`}
            >
              <Download className="w-4 h-4" />
              <span>{label}</span>
            </button>
            {i < visibleTemplates.length - 1 && <span className="text-gray-300">|</span>}
          </React.Fragment>
        ))}
      </div>
    </div>
  </div>
</div>

  <div className="space-y-3">
    {showTalkTrackSelector && (
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Talk Track</label>
        <select
          value={selectedTalkTrack}
          onChange={(e) => setSelectedTalkTrack(e.target.value)}
          disabled={!canUploadDocs && !isGlobalAdmin}
          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">-- Select talk track --</option>
          {STAFFING_TALK_TRACKS.map(t => (
            <option key={t.value} value={t.value}>{t.label}</option>
          ))}
        </select>
        <p className="text-xs text-gray-400 mt-1">Tag this KB so AI knows which talk track to use it for.</p>
      </div>
    )}
    <div className="flex items-center space-x-3">
      <div className="flex-1">
        {!selectedFile ? (
          <label
            htmlFor="pdf-upload-input"
            className={`flex items-center justify-center space-x-2 px-4 py-3 border-2 border-dashed rounded-lg transition-colors ${
              canUploadDocs 
                ? 'border-gray-300 hover:border-blue-400 hover:bg-blue-50 cursor-pointer' 
                : 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-60'
            }`}
          >
            <Upload className="w-5 h-5 text-gray-400" />
            <span className="text-sm text-gray-600 font-medium">
              {canUploadDocs ? 'Choose PDF or DOCX file' : 'Upload restricted'}
            </span>
          </label>
        ) : (
          <div className="flex items-center space-x-3 px-4 py-3 bg-blue-50 border-2 border-blue-200 rounded-lg">
            <FileText className="w-5 h-5 text-blue-600 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm text-blue-900 font-medium truncate">{selectedFile.name}</p>
              <p className="text-xs text-blue-600">
                {(selectedFile.size / 1024).toFixed(1)} KB • Ready to upload
              </p>
            </div>
            <button
              onClick={() => {
                setSelectedFile(null);
                const fileInput = document.getElementById('pdf-upload-input');
                if (fileInput) fileInput.value = '';
              }}
              className="text-blue-600 hover:text-blue-800 p-1 hover:bg-blue-100 rounded transition-colors"
              title="Remove file"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
        <input 
          type="file" 
          accept=".pdf,.docx"
          onChange={handleFileChange}
          disabled={!canUploadDocs}
          className="hidden"
          id="pdf-upload-input"
        />
      </div>
      <Button 
        onClick={handleUpload} 
        disabled={uploading || !selectedFile || (!canUploadDocs && !isGlobalAdmin) || (isGlobalAdmin && !selectedTenantId)}
        className="px-6"
      >
        {uploading ? (
          <div className="flex items-center space-x-2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            <span>Uploading...</span>
          </div>
        ) : (
          canUploadDocs ? 'Upload Document' : 'Upload Restricted'
        )}
      </Button>
    </div>
  </div>

  {documents.length > 0 ? (
    <div className="pt-4">
      <h3 className="font-medium mb-3 text-gray-900">Uploaded Documents ({documents.length})</h3>
      <ul className="space-y-3">
        {documents.map((doc) => (
          <li key={doc.id} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <p className="font-semibold text-gray-900 mb-1">{doc.title || doc.file_name}</p>
                <div className="flex items-center gap-2 mb-2">
                  <p className="text-xs text-gray-500">{doc.file_name}</p>
                  {doc.talk_track_type && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 font-medium">
                      {doc.talk_track_type === 'recruiting_candidates' ? 'Recruiting Candidates'
                        : doc.talk_track_type === 'recruiting_candidates_multi_role' ? 'Recruiting Candidates - Multi Role'
                        : doc.talk_track_type === 'acquiring_clients' ? 'B2B Client Acquisition'
                        : doc.talk_track_type === 'credentialing_services' ? 'Credentialing Services'
                        : doc.talk_track_type}
                    </span>
                  )}
                </div>
                {doc.content && (
                  <p className="text-gray-600 text-sm line-clamp-2">
                    {doc.content.slice(0, 200)}...
                  </p>
                )}
                {doc.created_at && (
                  <p className="text-xs text-gray-400 mt-2">
                    Uploaded: {new Date(doc.created_at).toLocaleDateString()}
                  </p>
                )}
              </div>
              <div className="flex items-center space-x-2 ml-4">
                {canUploadDocs && (
                  <button
                    onClick={() => handleToggleMaster(doc.id, doc.is_master)}
                    title={doc.is_master ? 'Remove from all campaigns' : 'Add to all campaigns automatically'}
                    className={`text-xs px-2 py-1 rounded font-medium border transition-colors ${
                      doc.is_master
                        ? 'bg-orange-100 text-orange-700 border-orange-200 hover:bg-orange-200'
                        : 'bg-gray-100 text-gray-500 border-gray-200 hover:bg-gray-200'
                    }`}
                  >
                    {doc.is_master ? 'Master' : 'Set Master'}
                  </button>
                )}
                <a href={doc.file_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 text-sm font-medium px-2 py-1 rounded hover:bg-blue-50"
                >
                  View
                </a>
                {canDeleteDocs && (
                  <button
                    onClick={() => handleDelete(doc.id)}
                    className="text-red-600 hover:text-red-800 p-1 rounded hover:bg-red-50"
                    title="Delete document"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  ) : (
    <div className="pt-4 text-center py-8">
      <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
      <p className="text-gray-600 font-medium mb-1">No documents uploaded yet</p>
      <p className="text-gray-500 text-sm mb-4">
        {canUploadDocs 
          ? 'Upload PDFs or DOCX files to give your AI the information it needs to have great conversations.'
          : 'Contact an admin to upload documents for AI context.'
        }
      </p>
      {canUploadDocs && (
        <button
          onClick={() => {
            handleDownloadSamplePDF();
            window.dispatchEvent(new CustomEvent('tour-template-downloaded'));
          }}
          className="tour-download-template inline-flex items-center space-x-2 text-sm text-blue-600 hover:text-blue-800 font-medium"
        >
          <Download className="w-4 h-4" />
          <span>Download a template to get started</span>
        </button>
      )}
    </div>
  )}
</Card>
      )}

      {/* Recently deleted: 30-day recycle bin. Restoring brings back the document
          AND the campaigns it was scoped to, not just the file. */}
      {kbActiveTab === 'documents' && deletedDocs.length > 0 && (
        <Card className="p-5 mt-4">
          <div className="flex items-center space-x-2 mb-1">
            <RotateCcw className="w-4 h-4 text-gray-500" />
            <h3 className="text-sm font-semibold text-gray-800">Recently deleted</h3>
          </div>
          <p className="text-xs text-gray-500 mb-4">
            Deleted documents can be restored for 30 days. Restoring also puts the document
            back on the campaigns it was on before.
          </p>

          <ul className="divide-y divide-gray-100">
            {deletedDocs.map((doc) => {
              const daysLeft = Math.max(
                0,
                Math.ceil((new Date(doc.expires_at) - new Date()) / 86400000)
              );
              const campaignNames = (doc.campaign_links || [])
                .map((l) => l.campaign_name)
                .filter(Boolean);

              return (
                <li key={doc.id} className="py-3 flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      <span className="text-sm font-medium text-gray-800 truncate">
                        {doc.title}
                      </span>
                      {doc.is_master && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-orange-100 text-orange-700 border border-orange-200">
                          Master
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Deleted {new Date(doc.archived_at).toLocaleDateString()} ·{' '}
                      {daysLeft} day{daysLeft === 1 ? '' : 's'} left to restore
                    </p>
                    {campaignNames.length > 0 && (
                      <p className="text-xs text-gray-500 mt-0.5 truncate">
                        Was on: {campaignNames.join(', ')}
                      </p>
                    )}
                  </div>

                  {canDeleteDocs && (
                    <button
                      onClick={() => handleRestore(doc.id)}
                      disabled={restoringId === doc.id}
                      className="flex-shrink-0 inline-flex items-center space-x-1 text-xs font-medium px-2.5 py-1.5 rounded border border-blue-200 text-blue-700 bg-blue-50 hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <RotateCcw className="w-3.5 h-3.5" />
                      <span>{restoringId === doc.id ? 'Restoring...' : 'Restore'}</span>
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        </Card>
      )}

      {kbActiveTab === 'websites' && (
        <Card className="p-5 space-y-4">
          {/* Contextual help for Websites tab */}
          <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="flex items-start space-x-3">
              <Lightbulb className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-gray-700">
                <span className="font-medium">Add your website</span> and we'll automatically extract content for your AI to reference. 
                Great for pulling in your About page, Services, or FAQ sections.
              </p>
            </div>
          </div>

          <div className="space-y-3">
            {showTalkTrackSelector && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Talk Track</label>
                <select
                  value={selectedTalkTrack}
                  onChange={(e) => setSelectedTalkTrack(e.target.value)}
                  disabled={!canUploadDocs && !isGlobalAdmin}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">-- Select talk track --</option>
                  {STAFFING_TALK_TRACKS.map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
                <p className="text-xs text-gray-400 mt-1">Tag this KB so AI knows which talk track to use it for.</p>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Website URL
              </label>
              <input
                type="url"
                value={websiteUrl}
                onChange={(e) => setWebsiteUrl(e.target.value)}
                placeholder="https://example.com"
                disabled={!canUploadDocs && !isGlobalAdmin}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Title (optional)
              </label>
              <input
                type="text"
                value={websiteTitle}
                onChange={(e) => setWebsiteTitle(e.target.value)}
                placeholder="My Company Website"
                disabled={!canUploadDocs && !isGlobalAdmin}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
            </div>

            <Button
              onClick={() => handleAddWebsite(false)}
              disabled={!websiteUrl || (!canUploadDocs && !isGlobalAdmin) || (isGlobalAdmin && !selectedTenantId)}
              className="w-full"
            >
              <Globe className="w-4 h-4 mr-2" />
              Add Website
            </Button>

            {/* The teaching moment. Two intents look identical when pasted and need
                opposite handling, so we ask instead of assuming. */}
            {assetPrompt && (
              <div className="mt-4 border border-blue-200 bg-blue-50 rounded-lg p-4">
                <p className="text-sm font-medium text-gray-900 mb-1">
                  Is this intel for the AI to discuss, or a document you want it to send to leads?
                </p>
                <p className="text-xs text-gray-600 mb-3 break-all">{assetPrompt.url}</p>

                <div className="space-y-3">
                  <div className="bg-white border border-gray-200 rounded-lg p-3">
                    <p className="text-sm font-medium text-gray-900">Intel to discuss</p>
                    <p className="text-xs text-gray-600 mt-1">
                      We read the page and the AI answers questions from it. Best for pages of text: your
                      services, pricing, an FAQ, an about page.
                    </p>
                    <Button
                      onClick={() => { setAssetPrompt(null); handleAddWebsite(true); }}
                      className="mt-2 text-xs px-3 py-1"
                    >
                      Read it for answers
                    </Button>
                  </div>

                  <div className="bg-white border border-gray-200 rounded-lg p-3">
                    <p className="text-sm font-medium text-gray-900">A document to send</p>
                    <p className="text-xs text-gray-600 mt-1">
                      We store the link and the AI sends it to a lead who asks. Best for photos, brochures,
                      deal sheets, spec sheets, menus, portfolios.
                    </p>

                    <div className="mt-3 space-y-2">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">What is it?</label>
                        <input
                          type="text"
                          value={assetLabel}
                          onChange={(e) => setAssetLabel(e.target.value)}
                          placeholder="Property photos"
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Which campaign sends it?</label>
                        <select
                          value={assetCampaignId}
                          onChange={(e) => setAssetCampaignId(e.target.value)}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                        >
                          <option value="">Choose a campaign</option>
                          {assetCampaigns.map((c) => (
                            <option key={c.id} value={c.id}>{c.name}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        {/* Required. A link with no trigger is what made a tenant say
                            "I had to push 3 times before it was shared." */}
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          When should the AI send it?
                        </label>
                        <input
                          type="text"
                          value={assetSendWhen}
                          onChange={(e) => setAssetSendWhen(e.target.value)}
                          placeholder="when a buyer asks for more information or photos"
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          In your own words. Without this the AI has to guess, and it will not send it reliably.
                        </p>
                      </div>
                      <Button
                        onClick={handleSaveShareableAsset}
                        disabled={savingAsset || !assetCampaignId || !assetSendWhen.trim()}
                        className="text-xs px-3 py-1"
                      >
                        {savingAsset ? 'Saving...' : 'Save it to send'}
                      </Button>
                    </div>
                  </div>
                </div>

                <button
                  onClick={() => setAssetPrompt(null)}
                  className="mt-3 text-xs text-gray-500 underline"
                >
                  Cancel
                </button>
              </div>
            )}
          </div>

          {websites.length > 0 ? (
            <div className="pt-4">
              <h3 className="font-medium mb-3 text-gray-900">Added Websites ({websites.length})</h3>
              <ul className="space-y-3">
                {websites.map((site) => (
                  <li key={site.id} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <p className="font-semibold text-gray-900 mb-1">{site.title}</p>
                        <a
                          href={site.website_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-blue-600 hover:underline mb-2 block"
                        >
                          {site.website_url}
                        </a>
                        {site.content && (
                          <p className="text-gray-600 text-sm line-clamp-2">
                            {site.content.slice(0, 200)}...
                          </p>
                        )}
                        {site.scrape_quality_score && (
                          <div className="mt-2 flex items-center space-x-2">
                            <span className="text-xs text-gray-500">Quality:</span>
                            <div className="flex-1 max-w-[100px] h-2 bg-gray-200 rounded-full overflow-hidden">
                              <div
                                className={`h-full ${
                                  site.scrape_quality_score >= 80
                                    ? 'bg-green-500'
                                    : site.scrape_quality_score >= 60
                                    ? 'bg-yellow-500'
                                    : 'bg-red-500'
                                }`}
                                style={{ width: `${site.scrape_quality_score}%` }}
                              />
                            </div>
                            <span className="text-xs text-gray-600">{site.scrape_quality_score}%</span>
                          </div>
                        )}
                        {site.created_at && (
                          <p className="text-xs text-gray-400 mt-2">
                            Added: {new Date(site.created_at).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center space-x-2 ml-4">
                        {canDeleteDocs && (
                          <button
                            onClick={() => handleDeleteWebsite(site.id)}
                            className="text-red-600 hover:text-red-800 p-1 rounded hover:bg-red-50"
                            title="Delete website"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <div className="pt-4 text-center py-8">
              <Globe className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 font-medium mb-1">No websites added yet</p>
              <p className="text-gray-500 text-sm">
                {canUploadDocs
                  ? 'Add your website URL and we\'ll extract the content for your AI to use.'
                  : 'Contact an admin to add websites for AI context.'
                }
              </p>
            </div>
          )}
        </Card>
      )}

      {kbActiveTab === 'builder' && (
        <div className="space-y-6">
          <Card className="p-6 bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200">
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center flex-shrink-0">
                <Brain className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  🤖 AI Knowledge Base Builder
                </h3>
                <p className="text-sm text-gray-700 mb-4">
                  Already have company documents? Upload them here and our AI will automatically extract and organize 
                  the key information into a knowledge base. Great for quickly converting existing materials.
                </p>
                <div className="flex items-center space-x-2 text-xs text-gray-600">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <span>Accepts: PDF, DOCX (up to 100 files at once)</span>
                </div>
              </div>
            </div>
          </Card>

          {!generatedKB ? (
            <>
              <Card className="p-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Upload Documents
                    </label>
                    <input
                      type="file"
                      multiple
                      accept=".pdf,.docx"
                      onChange={handleBulkFileChange}
                      disabled={!canUploadDocs && !isGlobalAdmin}
                      className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed"
                      id="bulk-upload-input"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      💡 Hold Ctrl (or Cmd on Mac) to select multiple files at once. Max 100 files. Supported: PDF, DOCX
                    </p>
                  </div>

                  {selectedFiles.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-gray-700">
                        Selected Files ({selectedFiles.length})
                      </p>
                      <div className="max-h-60 overflow-y-auto space-y-2 border border-gray-200 rounded-lg p-3">
                        {selectedFiles.map((file, index) => (
                          <div
                            key={index}
                            className="flex items-center justify-between p-2 bg-gray-50 rounded"
                          >
                            <div className="flex items-center space-x-2 flex-1 min-w-0">
                              <FileText className="w-4 h-4 text-gray-600 flex-shrink-0" />
                              <span className="text-sm text-gray-900 truncate">{file.name}</span>
                              {uploadProgress[index] !== undefined && (
                                <span className="text-xs text-green-600 ml-2">
                                  {uploadProgress[index]}%
                                </span>
                              )}
                            </div>
                            <button
                              onClick={() => {
                                const newFiles = selectedFiles.filter((_, i) => i !== index);
                                setSelectedFiles(newFiles);
                              }}
                              className="ml-2 flex-shrink-0 text-gray-400 hover:text-red-600"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <Button
                    onClick={handleGenerateKB}
                    disabled={selectedFiles.length === 0 || generating || (!canUploadDocs && !isGlobalAdmin) || (isGlobalAdmin && !selectedTenantId)}
                    className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white"
                  >
                    {generating ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Generating...
                      </>
                    ) : (
                      <>
                        <Wand2 className="w-4 h-4 mr-2" />
                        Generate Knowledge Base from {selectedFiles.length} File{selectedFiles.length !== 1 ? 's' : ''}
                      </>
                    )}
                  </Button>
                </div>
              </Card>

            </>
          ) : (
            <Card className="p-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="text-md font-semibold text-gray-900">
                    Generated Knowledge Base
                  </h4>
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>

                <textarea
                  value={generatedKB}
                  onChange={(e) => setGeneratedKB(e.target.value)}
                  rows={15}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                  placeholder="Your generated knowledge base will appear here..."
                />

                <p className="text-xs text-gray-500">
                  Review and edit the generated knowledge base above before saving.
                </p>

                <div className="flex space-x-3 mt-6">
                  <Button
                    onClick={handleSaveKB}
                    disabled={!canUploadDocs && !isGlobalAdmin}
                    className="flex-1 bg-green-600 hover:bg-green-700"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Save Knowledge Base
                  </Button>

                  <Button
                    onClick={() => {
                      setGeneratedKB('');
                      setSelectedFiles([]);
                      setUploadProgress({});
                    }}
                    variant="outline"
                    className="flex-1"
                  >
                    Start Over
                  </Button>
                </div>
              </div>
            </Card>
          )}
        </div>
      )}
    </div>
  );
};

export default KnowledgeBase;